import type {
  DeviceId,
  DeviceLocalCircleDataDeletionAuthorization,
  DeviceLocalCircleDataDeletionAuthorizationPayload,
  IdentityId,
  PublicKey
} from '@shared/types';
import type { SigningKey } from './ed25519Provider';
import { signRequestWithKey } from './signing';
import { verifySignedRequestWithPublicKey } from './deviceKeyBinding';

const AUTHORIZATION_TYPE = 'identity:device-local-circle-data-deletion-after-revoke';
const AUTHORIZATION_MAX_AGE_MS = 5 * 60 * 1000;
const AUTHORIZATION_MAX_FUTURE_SKEW_MS = 30 * 1000;

export async function signLocalCircleDataDeletionAuthorization(params: {
  identityId: IdentityId;
  targetDeviceId: DeviceId;
  identitySigningKey: SigningKey;
}): Promise<DeviceLocalCircleDataDeletionAuthorization> {
  return signRequestWithKey<DeviceLocalCircleDataDeletionAuthorizationPayload>(
    AUTHORIZATION_TYPE,
    {
      version: 1,
      purpose: 'device-local-circle-data-deletion-after-revoke-v1',
      identityId: params.identityId,
      targetDeviceId: params.targetDeviceId
    },
    params.identityId,
    params.identitySigningKey
  ) as Promise<DeviceLocalCircleDataDeletionAuthorization>;
}

export async function verifyLocalCircleDataDeletionAuthorization(params: {
  authorization: DeviceLocalCircleDataDeletionAuthorization | null | undefined;
  identityId: IdentityId;
  targetDeviceId: DeviceId;
  identityPublicKey: PublicKey;
  now?: number;
}): Promise<boolean> {
  const { authorization, identityId, targetDeviceId, identityPublicKey } = params;
  if (!authorization || authorization.type !== AUTHORIZATION_TYPE || authorization.signerId !== identityId) {
    return false;
  }
  const payload = authorization.payload;
  if (
    payload?.version !== 1
    || payload.purpose !== 'device-local-circle-data-deletion-after-revoke-v1'
    || payload.identityId !== identityId
    || payload.targetDeviceId !== targetDeviceId
  ) {
    return false;
  }
  const now = params.now ?? Date.now();
  if (
    authorization.timestamp > now + AUTHORIZATION_MAX_FUTURE_SKEW_MS
    || authorization.timestamp < now - AUTHORIZATION_MAX_AGE_MS
  ) {
    return false;
  }
  return verifySignedRequestWithPublicKey(authorization, identityPublicKey);
}
